"use client";

import { useCallback, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { useRoutePathname } from "@/hooks/use-route-pathname";
import { readParams, writeParams, type UrlCodec } from "@/lib/url-state";

/**
 * View state that lives in the query string: read through the codec, written
 * with the native `history.replaceState`, which the App Router syncs into
 * `useSearchParams` without a server round trip (menu.tsx uses
 * `router.replace` because its keys change the data the server renders; these
 * keys change only what the client shows, and a round trip cost 0.7 s per
 * click on the preview). One history entry per page, the default dropped
 * from the string. Hoist the codec out of the component, or memoize it, so
 * the setter keeps its identity.
 */
export function useUrlState<T>(codec: UrlCodec<T>): [T, (value: T) => void] {
  const searchParams = useSearchParams();
  const pathname = useRoutePathname();
  const value = useMemo(() => readParams(searchParams, codec), [searchParams, codec]);
  const set = useCallback(
    (next: T) => {
      // The live query string, not the render-time snapshot: two codecs written in one tick would otherwise drop each other's key.
      const qs = writeParams(new URLSearchParams(window.location.search), codec, next).toString();
      // A null state: the router copies its own internals onto it and syncs useSearchParams. Passing
      // the existing state back (it carries the router's marker) reads as an internal call and is not synced.
      window.history.replaceState(null, "", qs ? `${pathname}?${qs}` : pathname);
    },
    [codec, pathname],
  );
  return [value, set];
}
